import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/hooks/useUser";
import { supabase } from "@/lib/supabase";
import {
  User,
  Mail,
  Phone,
  Building,
  Briefcase,
  Globe,
  Clock,
  Shield,
  Bell,
  CreditCard,
  Loader2,
} from "lucide-react";
import { BookDemoDialog } from "@/components/BookDemoDialog";
import { useNavigate } from "react-router-dom";

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  language: string;
  timezone: string;
  notifications: {
    email: boolean;
    push: boolean;
    cases: boolean;
    updates: boolean;
  };
  security: {
    two_factor_enabled: boolean;
    last_password_change?: string;
  };
  billing: {
    plan: string;
    amount: number;
    interval: "monthly" | "yearly";
    card_last4?: string;
    card_expiry?: string;
  };
}

const Profile = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ProfileFormData>({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    language: "Español",
    timezone: "Europe/Madrid",
    notifications: {
      email: true,
      push: true,
      cases: true,
      updates: false,
    },
    security: {
      two_factor_enabled: false,
    },
    billing: {
      plan: "Pro",
      amount: 399900,
      interval: "monthly",
    },
  });

  // Add new state for security form
  const [securityForm, setSecurityForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [trialStatus, setTrialStatus] = useState<{
    isTrial: boolean;
    endsAt: string | null;
    planName: string | null;
  }>({
    isTrial: false,
    endsAt: null,
    planName: null,
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileData();
    fetchTrialStatus();
  }, [user]);

  const fetchProfileData = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        setFormData({
          name: data.full_name || "",
          email: data.email || user.email || "",
          phone: data.phone || "",
          company: data.company || "",
          role: data.role || "",
          language: data.language || "Español",
          timezone: data.timezone || "Europe/Madrid",
          notifications: data.notifications || {
            email: true,
            push: true,
            cases: true,
            updates: false,
          },
          security: data.security || {
            two_factor_enabled: false,
          },
          billing: data.billing || {
            plan: "Pro",
            amount: 399900,
            interval: "monthly",
          },
        });
      } else {
        // Initialize with user's email if no profile exists
        setFormData((prev) => ({
          ...prev,
          email: user.email || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast({
        title: "Error",
        description:
          "No se pudo cargar el perfil. Por favor, intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    if (!user) return;

    const { data: subscription, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("is_trial", true)
      .eq("status", "active")
      .single();

    if (error && error.code !== "PGRST116") {
      console.error("Error fetching trial status:", error);
      return;
    }

    if (subscription) {
      setTrialStatus({
        isTrial: true,
        endsAt: subscription.trial_ends_at,
        planName: subscription.plan_name,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle security form changes
  const handleSecurityInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setSecurityForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas nuevas no coinciden.",
      });
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: securityForm.newPassword,
      });

      if (error) throw error;

      toast({
        title: "Contraseña actualizada",
        description: "Tu contraseña ha sido actualizada exitosamente.",
      });

      // Clear form
      setSecurityForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error updating password:", error);
      toast({
        title: "Error",
        description:
          "No se pudo actualizar la contraseña. Por favor, intente nuevamente.",
        variant: "destructive",
      });
    }
  };

  // Handle 2FA toggle
  const handleToggle2FA = () => {
    navigate("/dashboard/settings?tab=security");
  };

  // Handle notification changes
  const handleNotificationToggle = (
    key: keyof typeof formData.notifications
  ) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
    // Save immediately
    handleSaveChanges();
  };

  // Handle plan change
  const handlePlanChange = async () => {
    toast({
      title: "Próximamente",
      description: "El cambio de plan estará disponible próximamente.",
    });
  };

  const handleSaveChanges = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // First check if profile exists
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", user.id)
        .single();

      const profileData = {
        id: user.id,
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        role: formData.role,
        language: formData.language,
        timezone: formData.timezone,
        notifications: formData.notifications,
        security: formData.security,
        billing: formData.billing,
        updated_at: new Date().toISOString(),
      };

      let error;
      if (!existingProfile) {
        // Insert new profile
        const { error: insertError } = await supabase
          .from("profiles")
          .insert([{ ...profileData, created_at: new Date().toISOString() }]);
        error = insertError;
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from("profiles")
          .update(profileData)
          .eq("id", user.id);
        error = updateError;
      }

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      toast({
        title: "Perfil actualizado",
        description: "Los cambios han sido guardados exitosamente.",
      });

      // Refresh the profile data after saving
      await fetchProfileData();
    } catch (error) {
      console.error("Error saving profile:", error);
      let errorMessage =
        "No se pudieron guardar los cambios. Por favor, intente nuevamente.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePlan = () => {
    navigate("/planes");
  };

  useEffect(() => {
    if (trialStatus.isTrial && trialStatus.endsAt) {
      const trialEnd = new Date(trialStatus.endsAt);
      if (trialEnd < new Date()) {
        toast({
          title: "Prueba gratuita finalizada",
          description:
            "Tu período de prueba ha terminado. Por favor, actualiza tu plan para continuar.",
          variant: "destructive",
        });
        navigate("/planes");
      }
    }
  }, [trialStatus, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Avatar className="h-20 w-20">
          <AvatarImage
            src={
              user?.user_metadata?.avatar_url || "https://github.com/shadcn.png"
            }
          />
          <AvatarFallback>
            {formData.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div>
          <h2 className="text-2xl font-bold text-legal-navy">
            {formData.name}
          </h2>
          <p className="text-gray-500">{formData.role}</p>
        </div>
      </div>

      <Tabs defaultValue="personal" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="personal">Información Personal</TabsTrigger>
          <TabsTrigger value="security">Seguridad</TabsTrigger>
          <TabsTrigger value="notifications">Notificaciones</TabsTrigger>
          <TabsTrigger value="billing">Facturación</TabsTrigger>
        </TabsList>

        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle>Información Personal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre Completo</Label>
                  <div className="relative">
                    <User className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <div className="relative">
                    <Phone className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa</Label>
                  <div className="relative">
                    <Building className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Cargo</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Idioma</Label>
                  <div className="relative">
                    <Globe className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="language"
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Zona Horaria</Label>
                  <div className="relative">
                    <Clock className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={handleSaveChanges}
                disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar Cambios"
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Seguridad de la Cuenta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Contraseña Actual</Label>
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    value={securityForm.currentPassword}
                    onChange={handleSecurityInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nueva Contraseña</Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={securityForm.newPassword}
                    onChange={handleSecurityInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    Confirmar Nueva Contraseña
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={securityForm.confirmPassword}
                    onChange={handleSecurityInputChange}
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={handlePasswordChange}
                  disabled={
                    !securityForm.currentPassword ||
                    !securityForm.newPassword ||
                    !securityForm.confirmPassword
                  }>
                  Cambiar Contraseña
                </Button>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Autenticación de Dos Factores</Label>
                    <p className="text-sm text-muted-foreground">
                      Añade una capa extra de seguridad a tu cuenta
                    </p>
                  </div>
                  <Switch
                    checked={formData.security?.two_factor_enabled || false}
                    onCheckedChange={handleToggle2FA}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Preferencias de Notificaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Notificaciones por Email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe actualizaciones importantes por correo
                    </p>
                  </div>
                  <Switch
                    checked={formData.notifications.email}
                    onCheckedChange={() => handleNotificationToggle("email")}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Notificaciones Push</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe notificaciones en tiempo real
                    </p>
                  </div>
                  <Switch
                    checked={formData.notifications.push}
                    onCheckedChange={() => handleNotificationToggle("push")}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Actualizaciones de Casos</Label>
                    <p className="text-sm text-muted-foreground">
                      Notificaciones sobre cambios en tus casos
                    </p>
                  </div>
                  <Switch
                    checked={formData.notifications.cases}
                    onCheckedChange={() => handleNotificationToggle("cases")}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Actualizaciones del Sistema</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe información sobre nuevas funciones
                    </p>
                  </div>
                  <Switch
                    checked={formData.notifications.updates}
                    onCheckedChange={() => handleNotificationToggle("updates")}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Plan Actual</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Plan Profesional</h3>
                    <p className="text-sm text-muted-foreground">
                      $39.900/mes - Facturación mensual
                    </p>
                  </div>
                  <Button variant="outline" onClick={handlePlanChange}>
                    Cambiar Plan
                  </Button>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Próxima facturación:{" "}
                    {new Date().toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Método de Pago</h3>
                <div className="flex items-center space-x-4 rounded-lg border p-4">
                  <CreditCard className="h-6 w-6 text-gray-500" />
                  <div>
                    <p className="font-medium">
                      •••• •••• •••• {formData.billing?.card_last4 || "4242"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Expira: {formData.billing?.card_expiry || "12/2024"}
                    </p>
                  </div>
                  <Button variant="outline" className="ml-auto">
                    Actualizar
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Historial de Facturación</h3>
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border p-4">
                      <div>
                        <p className="font-medium">Factura #{2024000 + i}</p>
                        <p className="text-sm text-muted-foreground">
                          {`${i}/2024`} - $399.900
                        </p>
                      </div>
                      <Button variant="outline" size="sm">
                        Descargar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
